/**
 * Settings → Maintenance agent: labels and wording for the API's figures
 * (api#944 settings, api#1012 metrics and shadow report).
 *
 * NOTHING HERE COMPUTES A METRIC. Every rate is the API's own percentage, shown
 * beside the numerator and denominator it came from, never alone. A zero
 * denominator is `—`: there was nothing to measure, which is not 0%.
 */
import { todayCalendarDate } from './calendar-date';
import type { MaintenanceAgentMode, MaintenanceFlagType } from '../types/maintenance-flags';

export const MAINTENANCE_MODES: MaintenanceAgentMode[] = ['off', 'shadow', 'suggest'];

export const MAINTENANCE_MODE_COPY: Record<MaintenanceAgentMode, { label: string; sentence: string }> = {
  off: { label: 'Off', sentence: "The agent doesn't check equipment." },
  shadow: {
    label: 'Shadow',
    sentence:
      'The agent checks equipment and jobs every day and records what it would have flagged. Nobody sees the flags. Used to measure it before switching it on.',
  },
  suggest: {
    label: 'Suggest',
    sentence:
      'Office staff see flags on equipment and jobs, and a warning when assigning equipment. It never changes equipment or a job, and never blocks dispatch or assignment.',
  },
};

/**
 * SUGGEST IS NOT OFFERED YET (operator, 2026-10-10). The live tenants are in a
 * 30-day shadow until 2026-11-07, and no tenant moves to Suggest before that
 * has been reviewed. The API's PUT accepts `suggest` today, so this is the only
 * thing holding it — flip it in a reviewed PR, never on a date the client reads
 * off its own clock.
 */
export const SUGGEST_AVAILABLE = false;
export const SUGGEST_LOCKED_NOTE = 'Available after testing';

/**
 * Flag types as the office reads them: the API's MAINTENANCE_FLAG_LABEL
 * (`lib/maintenance-watch-suppressions.ts`), capitalised, so a dismissal's
 * proposed rule and this page name a check the same way.
 */
export const MAINTENANCE_FLAG_TYPE_LABEL: Record<MaintenanceFlagType, string> = {
  inspection_due: 'Inspection due',
  inspection_overdue: 'Inspection overdue',
  usage_near_limit: 'Usage near its limit',
  usage_over_limit: 'Usage over its limit',
  cert_expiring: 'Certificate expiring',
  cert_expired: 'Certificate expired',
  schedule_due: 'Service due',
  schedule_overdue: 'Service overdue',
  inspection_failed_in_service: 'Failed inspection while in service',
  on_job_while_overdue: 'On a job while overdue',
  left_on_site_long: 'Left on site too long',
  assigned_not_recorded: 'Assigned but not recorded on a ticket',
  assignment_not_returned: 'Assignment not returned',
};

/** A flag type's label, or `—` for a type this build does not know. Never the raw key. */
export function maintenanceFlagTypeLabel(type: string | null | undefined): string {
  return type && Object.prototype.hasOwnProperty.call(MAINTENANCE_FLAG_TYPE_LABEL, type)
    ? MAINTENANCE_FLAG_TYPE_LABEL[type as MaintenanceFlagType]
    : '—';
}

/** Known types in the label map's order, then any type this build does not know. */
export function orderFlagTypes(types: string[]): string[] {
  const order = Object.keys(MAINTENANCE_FLAG_TYPE_LABEL);
  const rank = (t: string) => { const i = order.indexOf(t); return i < 0 ? order.length : i; };
  return [...types].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

/**
 * The tenant-local current month, 'YYYY-MM'. Null while the tenant zone is
 * unknown: the device's month is not the tenant's, and there is no Edmonton
 * default — a guessed month asks the API the wrong question.
 */
export function currentMetricMonth(timeZone: string | null | undefined, now: Date = new Date()): string | null {
  const today = todayCalendarDate(timeZone, now);
  return today ? today.slice(0, 7) : null;
}

const isCount = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/**
 * "7 of 9 (77.8%)" from the API's numerator, denominator and percentage.
 * `—` with no denominator or no percentage. Never a bare percentage.
 */
export function ratePhrase(num: unknown, den: unknown, pct: unknown): string {
  if (!isCount(num) || !isCount(den) || den <= 0 || !isCount(pct)) return '—';
  return `${num} of ${den} (${pct}%)`;
}

/** "3 of 12" — a count against what it was out of. `—` when the denominator is 0 or absent. */
export function countOfPhrase(num: unknown, den: unknown): string {
  if (!isCount(num) || !isCount(den) || den <= 0) return '—';
  return `${num} of ${den}`;
}

/** A bare count, or `—` when it did not arrive. A real 0 stays 0. */
export function countPhrase(n: unknown): string {
  return isCount(n) ? String(n) : '—';
}

/** "35.5%", or `—` for a null rate. */
export function pctPhrase(pct: unknown): string {
  return isCount(pct) ? `${pct}%` : '—';
}

const flags = (n: number) => (n === 1 ? 'flag' : 'flags');

/**
 * The pending line under the shadow report (operator, 2026-10-10): a flag
 * raised inside the scoring window has had no chance to be acted on yet, so a
 * low "acted on" share early in shadow is mostly flags still waiting. Null when
 * none are pending or the figure did not arrive.
 */
export function shadowPendingSentence(pending: unknown, windowDays: unknown): string | null {
  if (!isCount(pending) || pending <= 0 || !isCount(windowDays)) return null;
  return `${pending} ${flags(pending)} raised in the last ${windowDays} days ${pending === 1 ? 'is' : 'are'} still pending. `
    + `The office has ${windowDays} days from each flag to act, so ${pending === 1 ? 'it isn’t' : 'they aren’t'} counted in the share yet.`;
}
